import { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link } from "react-router-dom";
import { useAuth, SignUpButton } from "@clerk/clerk-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import {
  LucideArrowLeft,
  LucideEye,
  LucideEyeOff,
  LucideDownload,
  LucideChevronLeft,
  LucideChevronRight,
  LucideCheck,
  LucideX,
  LucideUsers,
  LucideSettings2,
} from "lucide-react";
import { PDFDocument } from "pdf-lib";
import { fill as fillPDF, SCHEMA_TO_PDF_FIELD, PAGE_1_SCHEMA_FIELDS, STEP_ANCHOR_FIELDS } from "./fill.js";
import templateUrl from "./template.pdf";
import { pdfjs } from "react-pdf";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url,
).toString();
import { BACKEND_URL } from "@/constants/api";
import { PRESETS } from "./presets.js";
import { formSchema, defaultValues } from "./schema.js";
import { twoPlans, fourPlans, threeResults, selectItems } from "./options.js";
import { STEPS, STEP_FIELDS, CORE_SUBJECTS, ARTS_ROWS } from "./steps.js";
import { LSWH_COLOR_MAP } from "../shared/colorSystem";
import { PillSelector } from "../shared/PillSelector";
import { PDFPreview } from "../shared/PDFPreview";
import { SectionHeading } from "../shared/SectionHeading";
import { AIGeneratedTextarea } from "../shared/AIGeneratedTextarea";
import { FormFieldGrid } from "../shared/FormFieldGrid";

const RESULT_COLOR_MAP = { PWD: "amber", PW: "blue", PVW: "emerald" };

// ─── Sub-components ───────────────────────────────────────────────────────────

function SubjectRow({ label, planField, resultField, plans, nameField, control }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
      <p className="font-semibold text-slate-800">{label}</p>
      {nameField && (
        <FormField
          control={control}
          name={nameField}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input placeholder={`${label} name`} {...field} />
              </FormControl>
            </FormItem>
          )}
        />
      )}
      <div className="space-y-1">
        <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">Plan</p>
        <FormField
          control={control}
          name={planField}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <PillSelector value={field.value} onChange={field.onChange} options={plans} colorMap={{}} />
              </FormControl>
            </FormItem>
          )}
        />
      </div>
      <div className="space-y-1">
        <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">Result</p>
        <FormField
          control={control}
          name={resultField}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <PillSelector value={field.value} onChange={field.onChange} options={threeResults} colorMap={RESULT_COLOR_MAP} />
              </FormControl>
            </FormItem>
          )}
        />
      </div>
    </div>
  );
}

function ArtsRow({ nameField, planField, resultField, placeholder, control }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
      <FormField
        control={control}
        name={nameField}
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-xs text-slate-500 font-medium uppercase tracking-wide">Subject Name</FormLabel>
            <FormControl>
              <Input placeholder={placeholder} {...field} />
            </FormControl>
          </FormItem>
        )}
      />
      <div className="space-y-1">
        <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">Plan</p>
        <FormField
          control={control}
          name={planField}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <PillSelector value={field.value} onChange={field.onChange} options={fourPlans} colorMap={{}} />
              </FormControl>
            </FormItem>
          )}
        />
      </div>
      <div className="space-y-1">
        <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">Result</p>
        <FormField
          control={control}
          name={resultField}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <PillSelector value={field.value} onChange={field.onChange} options={threeResults} colorMap={RESULT_COLOR_MAP} />
              </FormControl>
            </FormItem>
          )}
        />
      </div>
    </div>
  );
}

// ─── Step indicator ───────────────────────────────────────────────────────────

function StepIndicator({ steps, currentStep, completedSteps, onStepClick }) {
  return (
    <div className="flex items-start justify-between w-full">
      {steps.map((step, index) => {
        const isDone = completedSteps.has(step.id);
        const isCurrent = step.id === currentStep;
        const isLast = index === steps.length - 1;
        return (
          <div key={step.id} className="flex items-center flex-1">
            <div className="flex flex-col items-center">
              <button
                type="button"
                onClick={() => onStepClick(step.id)}
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold border-2 transition-all cursor-pointer ${
                  isDone && !isCurrent
                    ? 'bg-teal-700 border-teal-700 text-white hover:bg-teal-600'
                    : isCurrent
                    ? 'bg-teal-600 border-teal-600 text-white scale-110 ring-4 ring-inset ring-teal-400/50'
                    : 'bg-slate-100 border-slate-200 text-slate-400 hover:bg-slate-200'
                }`}
              >
                {isDone && !isCurrent ? <LucideCheck size={14} /> : step.id}
              </button>
              <span
                className={`text-xs mt-1 text-center leading-tight ${
                  isCurrent ? 'text-teal-700 font-bold' : isDone ? 'text-teal-600 font-medium' : 'text-slate-400 font-medium'
                }`}
              >
                {step.title}
              </span>
            </div>
            {!isLast && (
              <div className={`h-0.5 flex-1 mx-1 mb-4 ${isDone ? 'bg-teal-600' : 'bg-slate-200'}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export const FreeTrialReportCardProgressGr16202307 = ({ reportId, backUrl, contextInfo, defaultsGroupId, onFormChange, onRequestSave } = {}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [filledPDF, setFilledPDF] = useState(null);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showPostDownloadModal, setShowPostDownloadModal] = useState(false);
  const [isGeneratingAI, setIsGeneratingAI] = useState({ lswh: false, subjects: false });
  const [aiError, setAiError] = useState(null);
  const [lastChangedField, setLastChangedField] = useState(null);
  // scrollTargets[stepId] = { page: 1|2, topFraction: 0–1 } - fraction from top of page
  const [scrollTargets, setScrollTargets] = useState(
    Object.fromEntries(STEPS.map((s) => [s.id, { page: s.id <= 4 ? 1 : 2, topFraction: 0 }]))
  );
  const debounceRef = useRef(null);
  const regenDebounceRef = useRef(null);
  const fieldPositionsRef = useRef({});   // schemaField → { pageIndex, x, y, width, height }
  const lastChangedFieldRef = useRef(null);
  const formPaneRef = useRef(null);
  const { getToken } = useAuth();

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  // Load saved data on mount + generate initial PDF
  useEffect(() => {
    let initialValues;
    if (reportId) {
      getToken().then((token) =>
        fetch(`${BACKEND_URL}api/reports/${reportId}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
          .then((r) => r.json())
          .then((json) => {
            const values = json.data && Object.keys(json.data).length > 0 ? json.data : form.getValues();
            form.reset(values);
            fillPDF(values).then((url) => setFilledPDF(url));
          })
          .catch(() => fillPDF(form.getValues()).then((url) => setFilledPDF(url)))
      );
      return;
    }
    if (defaultsGroupId) {
      getToken().then((token) =>
        fetch(`${BACKEND_URL}api/report-groups/${defaultsGroupId}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
          .then((r) => r.json())
          .then((json) => {
            const values = { ...form.getValues(), ...(json.reportDefaults || {}) };
            form.reset(values);
            fillPDF(values).then((url) => setFilledPDF(url));
          })
          .catch(() => fillPDF(form.getValues()).then((url) => setFilledPDF(url)))
      );
      return;
    }
    const saved = localStorage.getItem("formData-progress-gr1-6-2023-07-v1");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        form.reset(parsed);
        initialValues = parsed;
      } catch {
        initialValues = form.getValues();
      }
    } else {
      initialValues = form.getValues();
    }
    fillPDF(initialValues).then((url) => setFilledPDF(url));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Build per-step scroll targets by inspecting field positions in the PDF template
  useEffect(() => {
    async function buildTargets() {
      try {
        const bytes = await fetch(templateUrl).then((r) => r.arrayBuffer());
        const pdfDoc = await PDFDocument.load(bytes);
        const pdfForm = pdfDoc.getForm();
        const pages = pdfDoc.getPages();

        const updated = {};
        for (const [stepStr, fieldName] of Object.entries(STEP_ANCHOR_FIELDS)) {
          const step = Number(stepStr);
          const pageIdx = step <= 4 ? 0 : 1;
          try {
            const field = pdfForm.getField(fieldName);
            const widgets = field.acroField.getWidgets();
            if (!widgets.length) throw new Error("no widgets");
            const rect = widgets[0].getRectangle();
            const pageH = pages[pageIdx].getHeight();
            // rect.y is from bottom in PDF pts; convert to 0–1 fraction from top (with 40pt padding)
            const topFromTop = Math.max(0, pageH - rect.y - rect.height - 40);
            const topFraction = Math.min(0.95, topFromTop / pageH);
            updated[step] = { page: pageIdx + 1, topFraction };
          } catch {
            updated[step] = { page: pageIdx + 1, topFraction: 0 };
          }
        }
        setScrollTargets((prev) => ({ ...prev, ...updated }));

        // Build per-field bounding boxes for yellow PDF highlight
        const positions = {};
        for (const [schemaField, pdfFieldName] of Object.entries(SCHEMA_TO_PDF_FIELD)) {
          const pageIdx = PAGE_1_SCHEMA_FIELDS.has(schemaField) ? 0 : 1;
          try {
            const f = pdfForm.getField(pdfFieldName);
            const widgets = f.acroField.getWidgets();
            if (!widgets.length) continue;
            const rect = widgets[0].getRectangle();
            positions[schemaField] = { pageIndex: pageIdx, x: rect.x, y: rect.y, width: rect.width, height: rect.height };
          } catch { /* field not in template, skip */ }
        }
        fieldPositionsRef.current = positions;
      } catch (e) {
        console.error("Failed to build PDF scroll targets:", e);
      }
    }
    buildTargets();
  }, []);

  // Auto-save, field highlight, and live PDF regeneration
  useEffect(() => {
    const subscription = form.watch((_, { name }) => {
      // Auto-save (300 ms)
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        if (reportId) {
          getToken().then((token) =>
            fetch(`${BACKEND_URL}api/reports/${reportId}`, {
              method: "PUT",
              headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
              body: JSON.stringify({ data: form.getValues() }),
            })
          );
        } else if (defaultsGroupId) {
          onFormChange?.(form.getValues());
        } else {
          localStorage.setItem("formData-progress-gr1-6-2023-07-v1", JSON.stringify(form.getValues()));
        }
      }, 300);

      // Track last changed field (state for UI, ref for async callback)
      if (name) {
        setLastChangedField(name);
        lastChangedFieldRef.current = name;
      }

      // Live PDF regeneration (500 ms debounce so we don't regen while typing)
      if (regenDebounceRef.current) clearTimeout(regenDebounceRef.current);
      setIsPdfGenerating(true);
      regenDebounceRef.current = setTimeout(async () => {
        const changedField = lastChangedFieldRef.current;
        const highlightRect = changedField ? (fieldPositionsRef.current[changedField] ?? null) : null;
        const url = await fillPDF(form.getValues(), highlightRect);
        setFilledPDF(url);
        setIsPdfGenerating(false);
      }, 500);
    });
    return () => {
      subscription.unsubscribe();
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (regenDebounceRef.current) clearTimeout(regenDebounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  const handleDownload = async () => {
    if (!filledPDF) return;
    const cleanUrl = await fillPDF(form.getValues(), null);
    const { studentName, oen } = form.getValues();
    const fileName = [studentName, oen].filter(Boolean).join("_").replace(/\s+/g, " ").trim() || "report-card";
    const link = document.createElement("a");
    link.href = cleanUrl;
    link.setAttribute("download", `${fileName}.pdf`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    if (!reportId && !defaultsGroupId) {
      setShowPostDownloadModal(true);
    }
  };

  const handleNext = () => {
    setCurrentStep((s) => Math.min(s + 1, STEPS.length));
    formPaneRef.current?.scrollTo({ top: 0 });
  };

  const handlePrev = () => {
    setCurrentStep((s) => Math.max(s - 1, 1));
    formPaneRef.current?.scrollTo({ top: 0 });
  };

  const toggleManualComplete = (stepId) => {
    const stepFields = STEP_FIELDS[stepId];
    const current = new Set(form.getValues('_completedFields') || []);
    const isComplete = stepFields.every((f) => current.has(f));
    if (isComplete) {
      stepFields.forEach((f) => current.delete(f));
    } else {
      stepFields.forEach((f) => current.add(f));
    }
    form.setValue('_completedFields', [...current]);
  };

  const handleGenerateAI = async (fieldName, aiKey) => {
    setAiError(null);
    const token = await getToken();
    if (!token) {
      setAiError("Sign in to use AI comment generation.");
      return;
    }
    setIsGeneratingAI((prev) => ({ ...prev, [aiKey]: true }));
    try {
      const values = form.getValues();
      const response = await fetch(`${BACKEND_URL}api/report-comments`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          reportId: reportId || "free-trial",
          strengths: values[fieldName] || "",
          nextSteps: values[fieldName] || "",
          studentInfo: {
            grade: values.grade,
            absent: values.daysAbsent,
            late: values.timesLate,
            responsibility: values.responsibility,
            organized: values.organization,
            independent: values.independentWork,
            collaboration: values.collaboration,
            initiative: values.initiative,
          },
        }),
      });
      if (response.status === 403) {
        setAiError("AI generation quota exceeded. Please upgrade your plan.");
        return;
      }
      if (!response.ok) {
        setAiError("Failed to generate AI comment. Please try again.");
        return;
      }
      const { comment } = await response.json();
      form.setValue(fieldName, comment);
    } catch {
      setAiError("Failed to generate AI comment. Please try again.");
    } finally {
      setIsGeneratingAI((prev) => ({ ...prev, [aiKey]: false }));
    }
  };

  // ── Step completion (all fields non-empty) ───────────────────────────────────
  const watchedValues = form.watch();
  const completedFields = new Set(watchedValues._completedFields || []);
  const completedSteps = new Set(
    STEPS.filter((step) =>
      STEP_FIELDS[step.id].every((f) => completedFields.has(f))
    ).map((step) => step.id)
  );

  // ── Step content ────────────────────────────────────────────────────────────
  const hl = () => "";
  const pillHl = () => "rounded-lg";

  const renderStep = () => {
    const { control } = form;

    if (currentStep === 1) {
      return (
        <div className="space-y-4">
          <SectionHeading title="Student Information" />
          <FormFieldGrid
            fields={[
              { name: "studentName", label: "Student Name" },
              { name: "oen",         label: "OEN" },
              { name: "date",        label: "Date" },
              { name: "grade",       label: "Grade" },
              { name: "teacher",     label: "Teacher" },
            ]}
            control={control}
          />
        </div>
      );
    }

    if (currentStep === 2) {
      return (
        <div className="space-y-4">
          <SectionHeading title="School Information" />
          <FormFieldGrid
            fields={[
              { name: "board",     label: "Board" },
              { name: "school",    label: "School" },
              { name: "address1",  label: "Address Line 1" },
              { name: "address2",  label: "Address Line 2" },
              { name: "principal", label: "Principal" },
              { name: "telephone", label: "Telephone" },
            ]}
            control={control}
          />
        </div>
      );
    }

    if (currentStep === 3) {
      return (
        <div className="space-y-4">
          <SectionHeading title="Attendance" />
          <FormFieldGrid
            fields={[
              { name: "daysAbsent",      label: "Days Absent (this term)", inputMode: "numeric" },
              { name: "totalDaysAbsent", label: "Total Days Absent",        inputMode: "numeric" },
              { name: "timesLate",       label: "Times Late (this term)",   inputMode: "numeric" },
              { name: "totalTimesLate",  label: "Total Times Late",         inputMode: "numeric" },
            ]}
            control={control}
            gridClassName="grid grid-cols-2 gap-6"
          />
          <FormField
            control={control}
            name="boardInformation1"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Board Information (Page 1)</FormLabel>
                <FormControl>
                  <Textarea {...field} className={`min-h-[80px] ${hl("boardInformation1")}`} />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
      );
    }

    if (currentStep === 4) {
      const SKILL_LABELS = {
        responsibility:  "Responsibility",
        organization:    "Organization",
        independentWork: "Independent Work",
        collaboration:   "Collaboration",
        initiative:      "Initiative",
        selfRegulation:  "Self-Regulation",
      };
      return (
        <div className="space-y-5">
          <SectionHeading title="Learning Skills & Work Habits" />
          <div className="grid grid-cols-2 gap-4">
            {Object.entries(SKILL_LABELS).map(([name, label]) => (
              <div key={name} className="rounded-xl border border-slate-200 bg-white p-4 space-y-2">
                <p className="font-semibold text-slate-700 text-sm">{label}</p>
                <FormField
                  control={control}
                  name={name}
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <PillSelector
                          value={field.value}
                          onChange={field.onChange}
                          options={selectItems}
                          colorMap={LSWH_COLOR_MAP}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
            ))}
          </div>
          <AIGeneratedTextarea
            control={control}
            name="strengthsNextStepsLSWH"
            label="Strengths & Next Steps - Learning Skills"
            isGenerating={isGeneratingAI.lswh}
            onGenerate={() => handleGenerateAI("strengthsNextStepsLSWH", "lswh")}
          />
          {aiError && <p className="text-sm text-red-600 mt-1">{aiError}</p>}
        </div>
      );
    }

    if (currentStep === 5) {
      return (
        <div className="space-y-4">
          <SectionHeading title="Subject Grades" />
          <div className="space-y-3">
            {CORE_SUBJECTS.map((subject) => (
              <SubjectRow key={subject.planField} control={control} {...subject} />
            ))}
          </div>
        </div>
      );
    }

    if (currentStep === 6) {
      return (
        <div className="space-y-5">
          <SectionHeading title="Arts & Final Comments" />

          <FormField
            control={control}
            name="artsHeader"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Arts Header</FormLabel>
                <FormControl>
                  <Input placeholder="e.g. The Arts" {...field} className={hl("artsHeader")} />
                </FormControl>
              </FormItem>
            )}
          />

          <div>
            <p className="text-sm font-semibold text-slate-600 mb-2">Arts Subjects</p>
            <div className="space-y-3">
              {ARTS_ROWS.map((row) => (
                <ArtsRow key={row.nameField} control={control} {...row} />
              ))}
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
            <p className="font-semibold text-slate-800">Optional Studies</p>
            <FormField
              control={control}
              name="optionalStudies"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-xs text-slate-500 font-medium uppercase tracking-wide">Subject Name</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g. Islamic Studies" {...field} className={hl("optionalStudies")} />
                  </FormControl>
                </FormItem>
              )}
            />
            <div className={`space-y-1 ${pillHl("optionalStudiesPlan")}`}>
              <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">Plan</p>
              <FormField
                control={control}
                name="optionalStudiesPlan"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <PillSelector value={field.value} onChange={field.onChange} options={fourPlans} colorMap={{}} />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
            <div className={`space-y-1 ${pillHl("optionalStudiesResult")}`}>
              <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">Result</p>
              <FormField
                control={control}
                name="optionalStudiesResult"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <PillSelector value={field.value} onChange={field.onChange} options={threeResults} colorMap={RESULT_COLOR_MAP} />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          </div>

          <AIGeneratedTextarea
            control={control}
            name="strengthsNextStepsSubjects"
            label="Strengths & Next Steps - Subjects"
            isGenerating={isGeneratingAI.subjects}
            onGenerate={() => handleGenerateAI("strengthsNextStepsSubjects", "subjects")}
          />
          {aiError && <p className="text-sm text-red-600 mt-1">{aiError}</p>}

          <FormField
            control={control}
            name="boardInformation2"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Board Information (Page 2)</FormLabel>
                <FormControl>
                  <Textarea {...field} className={`min-h-[80px] ${hl("boardInformation2")}`} />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
      );
    }

    return null;
  };

  const isShared = backUrl?.startsWith("/shared-classes");

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="h-[calc(100vh-4rem)] flex flex-col" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* Header bar */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-white shrink-0">
        <Link to={backUrl || "/"}>
          <Button variant="ghost" size="sm">
            <LucideArrowLeft size={16} className="mr-1.5" />
            Back
          </Button>
        </Link>
        <div className="text-center">
          <h1
            className="text-lg font-semibold text-slate-800"
            style={{ fontFamily: "'DM Serif Display', serif" }}
          >
            Ontario Progress Report Card
          </h1>
          {contextInfo && (
            <p className="text-xs text-slate-500 leading-none mt-0.5">
              {[contextInfo.grade && `Grade ${contextInfo.grade}`, contextInfo.schoolYear, contextInfo.oen && `OEN ${contextInfo.oen}`, contextInfo.studentName]
                .filter(Boolean).join(' · ')}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {defaultsGroupId && (
            <Button size="sm" onClick={() => onRequestSave?.(form.getValues())} className="bg-blue-600 hover:bg-blue-700 text-white border-blue-600">
              Save Defaults
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => setShowPreview((v) => !v)} className="gap-1.5">
    {showPreview ? <LucideEyeOff size={16} /> : <LucideEye size={16} />}
    <span className="text-xs">{showPreview ? "Hide PDF" : "Show PDF"}</span>
  </Button>
          <Button variant="outline" size="sm" onClick={handleDownload} disabled={!filledPDF || isPdfGenerating}>
            {isPdfGenerating ? <span className="text-xs">Updating…</span> : <LucideDownload size={16} />}
          </Button>
        </div>
      </div>

      {/* Shared-class banner */}
      {isShared && (
        <div className="flex items-center gap-2 px-5 py-1.5 bg-amber-50 border-b border-amber-200 text-xs text-amber-700 shrink-0">
          <LucideUsers size={12} className="shrink-0" />
          Shared class - changes you save are visible to the owner
        </div>
      )}

      {/* Class Defaults banner */}
      {defaultsGroupId && (
        <div className="flex items-center gap-2 px-5 py-2.5 bg-blue-50 border-b-2 border-blue-200 shrink-0">
          <LucideSettings2 size={15} className="shrink-0 text-blue-500" />
          <span className="text-sm font-medium text-blue-800">
            Class Defaults — <span className="font-normal">You&apos;re setting default values for all reports in this class. No individual student is being edited.</span>
          </span>
        </div>
      )}

      {/* Main content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Form panel */}
        <div
          className={`flex flex-col overflow-hidden transition-all duration-300 ${
            showPreview ? "w-[55%]" : "w-full"
          }`}
        >
          {/* Step indicator */}
          <div className="px-6 pt-5 pb-3 shrink-0">
            <StepIndicator
              steps={STEPS}
              currentStep={currentStep}
              completedSteps={completedSteps}
              onStepClick={setCurrentStep}
            />
          </div>

          {/* Free-trial preset toolbar */}
          {!reportId && !defaultsGroupId && (
            <div className="px-6 py-2 shrink-0 flex flex-wrap gap-2 border-b border-slate-100 bg-slate-50">
              {PRESETS.map((preset) => (
                <Button
                  key={preset.label}
                  type="button"
                  variant="outline"
                  size="sm"
                  className="text-xs transition-all duration-150 hover:scale-105 hover:shadow-md active:scale-95"
                  onClick={() => form.reset(preset.data)}
                >
                  {preset.label}
                </Button>
              ))}
            </div>
          )}

          {/* Step content */}
          <div ref={formPaneRef} className="flex-1 overflow-y-auto px-6 pb-4">
            <Form {...form}>
              <form>
                <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-6">
                  {renderStep()}
                </div>
              </form>
            </Form>
          </div>

          {/* Navigation buttons */}
          <div className="flex items-center justify-between px-6 py-3 border-t border-slate-100 bg-white shrink-0">
            <Button
              type="button"
              variant="outline"
              onClick={handlePrev}
              disabled={currentStep === 1}
            >
              <LucideChevronLeft size={16} className="mr-1" />
              Prev
            </Button>

            <div className="flex items-center gap-2">
              {!isShared && (
                <Button
                  type="button"
                  size="sm"
                  variant={completedSteps.has(currentStep) ? "default" : "outline"}
                  onClick={() => toggleManualComplete(currentStep)}
                  className={completedSteps.has(currentStep) ? "gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-600" : "gap-1.5"}
                >
                  <LucideCheck size={14} />
                  {completedSteps.has(currentStep) ? "Marked Complete" : "Mark Complete"}
                </Button>
              )}
              {currentStep < STEPS.length ? (
                <Button type="button" onClick={handleNext}>
                  Next
                  <LucideChevronRight size={16} className="ml-1" />
                </Button>
              ) : (
                <Button type="button" onClick={handleDownload} disabled={!filledPDF}>
                  <LucideDownload size={16} className="mr-1.5" />
                  Download PDF
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* PDF preview panel */}
        <div
          className={`border-l border-slate-200 transition-all duration-300 overflow-hidden ${
            showPreview ? "w-[45%]" : "w-0"
          }`}
        >
          {showPreview && (
            <PDFPreview
              src={filledPDF}
              scrollTarget={scrollTargets[currentStep]}
              defaultPageCount={2}
            />
          )}
        </div>
      </div>

      {/* Free-trial CTA overlay */}
      {showPostDownloadModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-sm w-full mx-4 overflow-hidden">
            <div className="p-6 pt-5">
              {/* Close */}
              <button
                onClick={() => setShowPostDownloadModal(false)}
                className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition-colors"
                aria-label="Close"
              >
                <LucideX size={16} />
              </button>

              {/* Heading */}
              <h2
                className="text-xl font-semibold text-slate-800 leading-snug mb-4"
                style={{ fontFamily: "'DM Serif Display', serif" }}
              >
                Download Complete! <br/> Take a look :)
              </h2>

              {/* Divider */}
              <div className="border-t border-slate-100 mb-4" />

              {/* CTA */}
              <SignUpButton mode="redirect">
                <button
                  className="w-full py-2.5 px-4 rounded-xl text-sm font-semibold text-white transition-all duration-150 hover:opacity-90 active:scale-[0.98]"
                  style={{ background: "linear-gradient(135deg, #1a5c4a 0%, #2d7a5f 100%)" }}
                >
                  Create free account
                </button>
              </SignUpButton>

              {/* Perks */}
              <ul className="mt-3 space-y-1.5">
                {[
                  "Collaborate with teachers to complete reports",
                  "Use Gen AI to support your comments",
                  "Automate dozens of fields - no manual typing",
                  "And much more\u2026",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2 text-xs text-slate-400">
                    <span className="mt-0.5 shrink-0 w-3.5 h-3.5 rounded-full bg-teal-50 flex items-center justify-center">
                      <LucideCheck size={8} className="text-teal-600" strokeWidth={3} />
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
